import { useCallback, useContext, useMemo, useRef } from "react";
import polylabel from "polylabel";
import { parse as WKTparse } from "wkt";
import { Localization } from "@viamap/viamap2-common";
import { SearchBarGeneral } from "src/components/SearchBarGeneral";
import { message } from "src/components/Message";
import { AddressAutocomplete, BFELookup } from "src/managers/SearchBarGeneralPlugin";
import { LayerFunc } from "src/managers/LayerFunc";
import { MitLatLng } from "src/managers/MapFacade";
import type { MapFacadeMaplibre } from "src/managers/MapFacadeMaplibre";
import { PropertyInfoInterface } from "src/managers/PropertyInfoInterface";
import { DataCollector } from "src/dataCollector/DataCollector";
import { GenerateGeomUtils } from "src/managers/GenerateGeomUtils";
import { MapitStateContext, MapitStateActionType, actionUpdateDataLayer } from "src/states/MapitState";
import { feature } from "@turf/helpers";
import { GlassButton } from "src/components/GlassButtons";
import { ActionTracker } from "src/common/managers/ActionTracker";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

type BfeSearchData = {
   BFEnummer: number;
   SFEnummer?: string | number;
   type: "SFE" | "BPFG" | "Ejerlejlighed";
   geometri?: string;
};

export function JPBSimpleSearchHeader(props: {
   mapFacade: MapFacadeMaplibre;
   onPinLayerReady: (layerId: number) => void;
   onShowAdvanced: () => void;
}) {
   const { state: mapitState, dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const pinLayerIdRef = useRef<number | null>(null);

   const placeOrUpdatePin = useCallback(
      (freshLayerInfo: ReturnType<typeof LayerFunc.createLayerInfoForOnePoint>) => {
         const zoomTo = () => {
            try {
               const f = freshLayerInfo.geoJson?.features?.[0];
               if (f?.geometry) {
                  props.mapFacade.zoomToData(feature(f.geometry), 150);
               }
            } catch {
               /* ignore zoom errors */
            }
         };

         if (pinLayerIdRef.current === null) {
            mapitStateDispatch({
               type: MapitStateActionType.AddDataLayer,
               payload: { layerInfo: freshLayerInfo, zoomToFocus: false, showAsDefault: true },
               callbacks: {
                  onLayerAdded: (created) => {
                     pinLayerIdRef.current = created.layerId;
                     props.onPinLayerReady(created.layerId);
                     zoomTo();
                  }
               }
            } as any);
            return;
         }

         const id = pinLayerIdRef.current;
         const cur = mapitState.layers[id];
         if (!cur) {
            pinLayerIdRef.current = null;
            mapitStateDispatch({
               type: MapitStateActionType.AddDataLayer,
               payload: { layerInfo: freshLayerInfo, zoomToFocus: false, showAsDefault: true },
               callbacks: {
                  onLayerAdded: (created) => {
                     pinLayerIdRef.current = created.layerId;
                     props.onPinLayerReady(created.layerId);
                     zoomTo();
                  }
               }
            } as any);
            return;
         }

         mapitStateDispatch(
            actionUpdateDataLayer(id, {
               ...cur,
               geoJson: freshLayerInfo.geoJson,
               datasetname: freshLayerInfo.datasetname,
               propertiesInGeoJson: freshLayerInfo.propertiesInGeoJson,
               propertiesToDisplay: freshLayerInfo.propertiesToDisplay
            })
         );
         props.onPinLayerReady(id);
         zoomTo();
      },
      [mapitState.layers, mapitStateDispatch, props]
   );

   const onBfeSelected = useCallback(
      async (data: BfeSearchData) => {
         ActionTracker.track("JPBModule", "Search", "BFE");
         const layerName =
            data.BFEnummer +
            ` (${data.type === "Ejerlejlighed" ? Localization.getText("Condominium") : data.type})`;

         if (data.geometri) {
            const tmp = WKTparse(data.geometri);
            if (tmp?.coordinates[0]) {
               const coords = GenerateGeomUtils.convertFromUTMtoLatLng({
                  x: tmp.coordinates[0],
                  y: tmp.coordinates[1]
               });
               const layerInfo = LayerFunc.createLayerInfoForOnePoint(
                  new MitLatLng(coords.lat, coords.lng),
                  layerName,
                  { ...data }
               );
               placeOrUpdatePin(layerInfo);
               return;
            }
         }

         const dataCopy = { ...data };
         if ("geometri" in dataCopy) {
            delete (dataCopy as BfeSearchData).geometri;
         }

         if (dataCopy.type) {
            const EBR = await DataCollector.DF_GQL.EBR.EBRAdresseOgPunkt([dataCopy.BFEnummer.toString()]);
            if (EBR[0]?.geometri) {
               const tmp = WKTparse(EBR[0]?.geometri);
               if (tmp?.coordinates[0]) {
                  const coords = GenerateGeomUtils.convertFromUTMtoLatLng({
                     x: tmp.coordinates[0],
                     y: tmp.coordinates[1]
                  });
                  const layerInfo = LayerFunc.createLayerInfoForOnePoint(
                     new MitLatLng(coords.lat, coords.lng),
                     layerName,
                     { ...dataCopy }
                  );
                  placeOrUpdatePin(layerInfo);
                  return;
               }
            }
         }

         if (dataCopy.type === "SFE") {
            const geoJson = await PropertyInfoInterface.getGeojsonOfSFE(dataCopy.BFEnummer);
            const coords = polylabel(geoJson.features[0].geometry.coordinates, 0.00001);
            const layerInfo = LayerFunc.createLayerInfoForOnePoint(
               new MitLatLng(coords[1], coords[0]),
               layerName,
               { ...geoJson.features[0].properties }
            );
            placeOrUpdatePin(layerInfo);
            return;
         }

         message.info(Localization.getText("No Position found for BFE"));
      },
      [placeOrUpdatePin]
   );

   const onAddressSelected = useCallback(
      async (data: any) => {
         ActionTracker.track("JPBModule", "Search", "Adresse");
         const wgsCoords = data.geometri.coordinates[0].toReversed();
         const layerInfo = LayerFunc.createLayerInfoForOneAddressV2(data.vejnavn, data.husnummer, wgsCoords.reverse());
         placeOrUpdatePin(layerInfo);
      },
      [placeOrUpdatePin]
   );

   const autocompleters = useMemo(
      () => [new BFELookup(onBfeSelected), new AddressAutocomplete(onAddressSelected)],
      [onBfeSelected, onAddressSelected]
   );

   return (
      <div className="jpb-simple-search-header">
         <div className="jpb-simple-search-header__search">
            <SearchBarGeneral
               showWindow={true}
               autocompleters={autocompleters}
               callbackOnSelection={() => {}}
            />
         </div>
         <GlassButton
            className="jpb-view-mode-btn"
            title={Localization.getText("Advanced view")}
            onClick={() => props.onShowAdvanced()}
         >
            <MIBIcon icon="advancedMode" />
            <span className="jpb-view-mode-btn__label">{Localization.getText("Advanced")}</span>
         </GlassButton>
      </div>
   );
}
